import { OverlayContainer } from '@angular/cdk/overlay';
import { AfterViewInit, Component, Inject, Injector, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { AODADialog } from '@core/utilities/aoda-dialog';
import { Utils } from '@core/utilities/utils';
import { BaseComponent } from '@shared/components/base.component';
import { DialogData } from '@shared/components/confirmation-dialog/DialogData';
import { InProgressWorksheetTypeIds, NAWorksheetTypeIds } from '@shared/models/common/worksheet-type-ids.enum';
import { WorksheetType } from '@shared/models/worksheet/WorksheetType';
import { environment } from 'src/environments/environment';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelect, MatOption, MatOptgroup } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

interface WorksheetTypeGroup {
  category: number;
  worksheetTypes: WorksheetType[];
}

@Component({
  selector: 'app-create-new-dialog',
  templateUrl: './create-new-dialog.component.html',
  styleUrls: ['./create-new-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatSelect,
    FormsModule,
    ReactiveFormsModule,
    MatOption,
    MatOptgroup,
    MatDialogActions,
    MatButton,
    AsyncPipe,
    TranslatePipe
  ]
})
export class CreateNewDialogComponent extends BaseComponent implements OnInit, AfterViewInit {
  data: DialogData;
  worksheetTypeGroups: WorksheetTypeGroup[] = [];
  worksheetType = new UntypedFormControl();
  worksheetTypes: WorksheetType[];
  toppings = new UntypedFormControl();
  #overlayContainerElement: HTMLElement;
  #overlayContainer: OverlayContainer;

  constructor(
    private readonly injector: Injector,
    private readonly dialogRef: MatDialogRef<CreateNewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: DialogData
  ) {
    super(injector);
    this.data = data;
    this.dialogRef.disableClose = true;
    this.#overlayContainer = this.injector.get(OverlayContainer);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initWorksheetTypes();
    this.#setupoverlayContainer();
  }

  ngAfterViewInit(): void {
    AODADialog.applyAllFixes();
  }

  #setupoverlayContainer(): void {
    this.#overlayContainerElement = this.#overlayContainer.getContainerElement();
    this.#overlayContainerElement.classList.add('createNewDialogComponent');
  }

  initWorksheetTypes(): void {
    this.worksheetTypes = this.cache.worksheetTypes;
    if (this.worksheetTypes && this.worksheetTypes.length > 0) {
      const worksheetCategories = this.worksheetTypes
        .map(v => v.displayCategory)
        .filter((v, index, self) => self.indexOf(v) === index)
        .sort();
      worksheetCategories.forEach(v => {
        const wtGroup: WorksheetTypeGroup = {
          category: v,
          worksheetTypes: this.getWorksheetTypesByCategory(v)
        };
        this.worksheetTypeGroups.push(wtGroup);
      });

      if (this.data?.defaultWorksheetTypeId) {
        this.worksheetType.patchValue(this.data?.defaultWorksheetTypeId?.toLowerCase());
      }
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  isInprogress(worksheetTypeId: string): boolean {
    return InProgressWorksheetTypeIds.find(v => v.toLowerCase() === worksheetTypeId?.toLowerCase())?.length > 0;
  }

  isUnavailable(worksheetTypeId: string): boolean {
    return NAWorksheetTypeIds.find(v => v.toLowerCase() === worksheetTypeId?.toLowerCase())?.length > 0;
  }

  isDisabled(worksheetTypeId: string): boolean {
    return this.isUnavailable(worksheetTypeId) || (this.isProd && this.isInprogress(worksheetTypeId));
  }

  createWorksheet(): void {
    this.dialogRef.close(this.currentWorksheetTypeName);
  }

  private getWorksheetTypesByCategory(category: number): WorksheetType[] {
    return this.worksheetTypes.filter(v => v.displayCategory === category).sort((a, b) => a.displayOrder - b.displayOrder);
  }

  get isProd(): boolean {
    return environment.isProdEnv;
  }

  get worksheetTypeDetails(): string {
    const worksheetType = this.worksheetTypes.find(v => v.id.toLocaleLowerCase() === this.worksheetType.value?.toLowerCase());
    return worksheetType ? worksheetType.details[this.languageService.languageType] : undefined;
  }

  get currentWorksheetTypeName(): string {
    return this.cache.worksheetTypes.find(v => Utils.matchStr(v.id, this.worksheetType.value))?.typeName;
  }

  get canCreate(): boolean {
    return this.currentWorksheetTypeName && !this.isDisabled(this.worksheetType.value);
  }
}
